import React from 'react';
import { StoryHeadline, Reveal, StoryPhoto, usePick, bodyText, eyebrowText } from './StoryShared';
import { QueueLine, ConnectedSystems } from './StoryVisuals';
import { act1, act2, storyImages } from '../../data/storyPage';

/* Act 1 (the waiting line) + Act 2 (the bigger vision, the stumble, the lesson) */
export function StoryProblem() {
  const pick = usePick();
  return <>
      {/* Act 1 — opening */}
      <section className="px-6 pt-10 lg:pt-16" aria-labelledby="story-title" data-id="element-6692" data-mp="1pywnu8csj734">
        <div className="max-w-7xl mx-auto" data-id="element-6693" data-mp="1kzabe14sduvd">
          <Reveal className="grid grid-cols-1 lg:grid-cols-[1.25fr_1fr] gap-8 lg:gap-16 items-end" data-id="element-6694" data-mp="1l0lfk22594n0">
            <div data-id="element-6695" data-mp="1zxchoj37cugb">
              <p className={eyebrowText} data-id="element-6696" data-mp="1ztoo5jxrc81l">{pick(act1.eyebrow)}</p>
              <StoryHeadline id="story-title" as="h1" size="xl" lines={act1.headline} className="mt-5" data-id="element-6697" data-mp="1uw56ea1upd4w" />
            </div>
            <div className="space-y-4 lg:pb-2" data-id="element-6698" data-mp="3bdaxr6wssgr1">
              {act1.body.map(b => <p key={b.fr} className={bodyText} data-id="element-6699" data-mp="36bty1eq1v0c1">{pick(b)}</p>)}
            </div>
          </Reveal>
          <Reveal delay={0.05} className="mt-10 lg:mt-14" data-id="element-6700" data-mp="38xhxz4bf4m6c">
            <StoryPhoto src={storyImages.queue} alt={pick(act1.imageAlt)} aspect="aspect-[4/3] md:aspect-[21/9]" data-id="element-6701" data-mp="3dtf82sw38wao" />
          </Reveal>
        </div>
      </section>

      {/* 400 → 5 */}
      <section className="px-6 py-16 lg:py-20" data-id="element-6702" data-mp="3mvg980ug02hm">
        <Reveal className="max-w-6xl mx-auto" data-id="element-6703" data-mp="3mt2czoo10k8h">
          <QueueLine data-id="element-6704" data-mp="2q5p1v111ykue" />
        </Reveal>
      </section>

      {/* Transition statement */}
      <section className="px-6 pb-20 lg:pb-28" data-id="element-6705" data-mp="2v3h68uivvzfh">
        <Reveal className="max-w-6xl mx-auto border-t border-[#ECEDF2] pt-16 lg:pt-20" data-id="element-6706" data-mp="2v48nf0bqhum8">
          <StoryHeadline as="p" size="lg" lines={act1.transition} data-id="element-6707" data-mp="2z412lvvmuz0k" />
        </Reveal>
      </section>

      {/* Act 2 — the bigger vision */}
      <section className="px-6 py-16 lg:py-20 bg-[#FAFAFD]" aria-labelledby="story-connect" data-id="element-6708" data-mp="27eec4n72ce43">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-12 lg:gap-20 items-center" data-id="element-6709" data-mp="2bfzxyy1ks2qf">
          <Reveal data-id="element-6710" data-mp="3eqnjnaq99zh0">
            <StoryHeadline id="story-connect" size="md" lines={act2.headline} data-id="element-6711" data-mp="3eq5d9l9vq4vd" />
            <div className="mt-6 space-y-4 max-w-[500px]" data-id="element-6712" data-mp="39s3yhcjhkrpp">
              {act2.body.map(b => <p key={b.fr} className={bodyText} data-id="element-6713" data-mp="3oggrc0na1ufz">{pick(b)}</p>)}
            </div>
          </Reveal>
          <Reveal delay={0.05} data-id="element-6714" data-mp="3jhdommurs0b6">
            <ConnectedSystems data-id="element-6715" data-mp="3ji5hhpwzfjxr" />
          </Reveal>
        </div>
        <Reveal className="max-w-6xl mx-auto mt-14 lg:mt-20" data-id="element-6716" data-mp="2vr9leglhu2kp">
          <StoryHeadline as="p" size="xl" lines={act2.early} data-id="element-6717" data-mp="2vtw1j4mg9qej" />
        </Reveal>
      </section>

      {/* Act 2 — the stumble */}
      <section className="px-6 py-16 lg:py-24" aria-label={pick(act2.pivot[0])} data-id="element-6718" data-mp="34t94deg2jgqa">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-10 lg:gap-16 items-center" data-id="element-6719" data-mp="30re2o8u871bz">
          <Reveal className="order-2 lg:order-1" data-id="element-6720" data-mp="k1z2rxzalgaa">
            <StoryPhoto src={storyImages.rebuild} alt={pick(act2.imageAlt)} data-id="element-6721" data-mp="k4lco49no7vo" />
          </Reveal>
          <Reveal delay={0.05} className="order-1 lg:order-2" data-id="element-6722" data-mp="g0na1jl4gexv">
            <div className="space-y-4 max-w-[480px]" data-id="element-6723" data-mp="njehbzkab573">
              {act2.stumble.map(b => <p key={b.fr} className={bodyText} data-id="element-6724" data-mp="skkb80pokyi7">{pick(b)}</p>)}
            </div>
            <p className="mt-8 font-serif text-[1.6rem] lg:text-[1.9rem] leading-[1.2] text-[#121A3A]" data-id="element-6725" data-mp="dnbh4z8ztgms">
              <span className="block" data-id="element-6726" data-mp="imeahmfm1yex">{pick(act2.pivot[0])}</span>
              <span className="block text-[#4F46E5]" data-id="element-6727" data-mp="16b7bhxb3awge">{pick(act2.pivot[1])}</span>
            </p>
          </Reveal>
        </div>
      </section>

      {/* Founding principle */}
      <section className="px-6 py-20 lg:py-28 bg-[#F6F5FF]" aria-labelledby="story-lesson" data-id="element-6728" data-mp="1bbv06cbuj17g">
        <Reveal className="max-w-5xl mx-auto text-center" data-id="element-6729" data-mp="wj1j4mgxed0o">
          <p className="mx-auto max-w-[560px] font-sans text-[16px] md:text-[17px] text-[#5A6178]" data-id="element-6730" data-mp="11j5rtqble3gc">{pick(act2.lessonIntro)}</p>
          <StoryHeadline id="story-lesson" size="xl" lines={act2.lesson} className="mt-8" data-id="element-6731" data-mp="1p8gujdci67x4" />
        </Reveal>
      </section>
    </>;
}